import {
  Container,
  Card,
  Table,
  Badge,
  Button,
  Group,
  Stack,
  Title,
  Text,
  Loader,
  TextInput
} from "@mantine/core";

import axios from "axios";
import { useParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import useFetch from "../../clients/features/get_withcredentials";

const Bills = () => {

  const { hotelId } = useParams();

  const { data = [], refreshData } = useFetch(
    `https://api.komorotel.com/api/reservation/${hotelId}`
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reservations, setReservations] = useState([]);

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {

    const fetchReservations = async () => {

      try {

        const res = await axios.get(
          `https://api.komorotel.com/api/reservation/${hotelId}/pending-cancellations`,
          { withCredentials: true }
        );

        setReservations(res.data);

      } catch (err) {

        setError(err.message);

      } finally {

        setLoading(false);

      }

    };

    fetchReservations();

  }, [hotelId]);

  const calculateNights = (checkInDate, checkOutDate) => {

    const start = new Date(checkInDate);
    const end = new Date(checkOutDate);

    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));

  };

  const toggleSort = (field) => {

    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }

  };

  const processedReservations = useMemo(() => {

    let filtered = data.filter((reservation) => {

      const text = search.toLowerCase();

      return (
        reservation.guest?.firstName?.toLowerCase().includes(text) ||
        reservation.guest?.lastName?.toLowerCase().includes(text) ||
        reservation.roomId?.title?.toLowerCase().includes(text) ||
        reservation._id?.toLowerCase().includes(text)
      );

    });

    filtered.sort((a, b) => {

      let valueA;
      let valueB;

      if (sortField === "guest") {
        valueA = a.guest?.lastName || "";
        valueB = b.guest?.lastName || "";
      }

      if (sortField === "createdAt") {
        valueA = new Date(a.createdAt);
        valueB = new Date(b.createdAt);
      }

      if (sortField === "price") {
        valueA = a.totalPrice;
        valueB = b.totalPrice;
      }

      if (sortField === "room") {
        valueA = a.roomId?.title || "";
        valueB = b.roomId?.title || "";
      }

      if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
      if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;

      return 0;

    });

    return filtered;

  }, [data, search, sortField, sortDirection]);

  const handleValidateReservation = async (reservationId) => {

    try {

      setLoading(true);

      await axios.put(
        `https://api.komorotel.com/api/reservation/validate-requests/${reservationId}`,
        {},
        { withCredentials: true }
      );

      alert("Réservation confirmée");

    } catch (err) {

      console.error(err);

    } finally {

      refreshData();
      setLoading(false);

    }

  };

  const handleCancelReservation = async (reservationId, userId) => {

    try {

      setLoading(true);

      await axios.post(
        `https://api.komorotel.com/api/reservation/${userId}/cancellation-request`,
        { reservationId },
        { withCredentials: true }
      );

      alert("Demande d'annulation envoyée");

    } catch (err) {

      console.error(err);

    } finally {

      refreshData();
      setLoading(false);

    }

  };

  const handleDeleteReservation = async (requestId) => {

    try {

      setLoading(true);

      await axios.put(
        `https://api.komorotel.com/api/reservation/cancellation-requests/${requestId}`,
        { status: "approved" },
        { withCredentials: true }
      );

      alert("Réservation annulée définitivement");

    } catch (err) {

      console.error(err);

    } finally {

      refreshData();
      setLoading(false);

    }

  };

  return (

    <Container size="lg">

      <Stack gap="lg">

        <div>

          <Title order={2}>Réservations</Title>

          <Text c="dimmed" size="sm">
            Gérez les réservations de votre hôtel
          </Text>

        </div>

        <TextInput
          placeholder="Rechercher client, chambre ou ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {loading ? (

          <Group justify="center">
            <Loader />
          </Group>

        ) : error ? (

          <Text c="red">{error}</Text>

        ) : (

          <Card shadow="sm" radius="md" withBorder>

            <Table highlightOnHover>

              <thead>

                <tr>

                  <th onClick={() => toggleSort("id")}>ID</th>
                  <th onClick={() => toggleSort("guest")}>Client</th>
                  <th onClick={() => toggleSort("createdAt")}>Date</th>
                  <th>Nuits</th>
                  <th onClick={() => toggleSort("room")}>Chambre</th>
                  <th onClick={() => toggleSort("price")}>Prix</th>
                  <th>Status</th>
                  <th>Actions</th>
                  <th>Annulation</th>

                </tr>

              </thead>

              <tbody>

                {processedReservations.map((reservation) => {

                  const cancellationRequest = reservations?.find(
                    r => String(r.reservationId._id) === String(reservation._id)
                  );
                  const displayId =
                    reservation?.reservationId ||
                    reservation._id.slice(0,6).toUpperCase();

                  return (

                    <tr key={reservation._id}>

                      <td>{displayId}</td>

                      <td>
                        {reservation.guest?.firstName} {reservation.guest?.lastName}
                      </td>

                      <td>
                        {new Date(reservation.createdAt).toLocaleDateString("fr-FR")}
                      </td>

                      <td>
                        {calculateNights(
                          reservation.checkInDate,
                          reservation.checkOutDate
                        )}
                      </td>

                      <td>{reservation.roomId?.title}</td>

                      <td>
                        <strong>{reservation.totalPrice} €</strong>
                      </td>

                      <td>

                        {reservation.canceled ? (
                          <Badge color="red">Annulée</Badge>
                        ) : reservation.validated ? (
                          <Badge color="green">Validée</Badge>
                        ) : (
                          <Badge color="yellow">En attente</Badge>
                        )}

                      </td>

                      <td>

                        <Group gap="xs">

                          {!reservation.validated && !reservation.canceled && (

                            <Button
                              size="xs"
                              color="green"
                              onClick={() =>
                                handleValidateReservation(reservation._id)
                              }
                            >
                              Confirmer
                            </Button>

                          )}

                          {!reservation.canceled && (

                            <Button
                              size="xs"
                              color="red"
                              variant="light"
                              onClick={() =>
                                handleCancelReservation(
                                  reservation._id,
                                  reservation.guest?.id
                                )
                              }
                            >
                              Client absent
                            </Button>

                          )}

                        </Group>

                      </td>

                      <td>

                        {reservation.status=='pending_cancel' ? (

                          <Button
                            size="xs"
                            color="red"
                            onClick={() =>
                              handleDeleteReservation(cancellationRequest._id)
                            }
                          >
                            Annuler définitivement
                          </Button>

                        ) : (

                          <Text size="sm" c="dimmed">
                            Aucune
                          </Text>

                        )}

                      </td>

                    </tr>

                  );

                })}

              </tbody>

            </Table>

          </Card>

        )}

      </Stack>

    </Container>

  );

};

export default Bills;